import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, User } from 'lucide-react';
import { contentApi } from '../../services/contentApi';
import { eventCatalogCache } from '../../services/eventCatalogCache';
import { Category, EventListItem } from '../../types/content';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import { PageLoading } from '../../components/UI/LoadingOverlay';
import CourseFee from '../../components/UI/CourseFee';
import CatalogFilterLayout, { CatalogFilterSelect } from '../../components/content/CatalogFilterLayout';
import CourseCoverImage from '../../components/content/CourseCoverImage';
import { COURSE_IMAGES } from '../../config/courseImages';
import HeroBackdrop from '../../components/UI/HeroBackdrop';
import { heroImageFade, heroShell } from '../../content/heroThemes';
import { formatEventWhen, speakerHeadline } from '../../utils/eventDisplay';

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<EventListItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    contentApi.listCategories('Event').then((r) => setCategories(r.data.categories || [])).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!eventCatalogCache.getList()) setLoading(true);
    contentApi.listEvents({
      search: search || undefined,
      categoryId: categoryId || undefined,
      pageSize: 24,
    }).then((r) => {
      if (cancelled) return;
      setEvents(r.data.events || []);
      setError(null);
    }).catch(() => {
      if (!cancelled) setError('Unable to load events.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [search, categoryId]);

  return (
    <div>
      <section className={heroShell('events')}>
        <HeroBackdrop tone="events" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[1.35rem] md:py-[1.575rem] lg:py-[1.8rem]">
          <div className="grid lg:grid-cols-2 gap-[1.125rem] lg:gap-[1.8rem] items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">Meet. Learn. Connect.</p>
              <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight max-w-3xl">IDCoder events</h1>
              <p className="mt-4 text-sm sm:text-base text-primary-100/90 leading-relaxed">
                Workshops, meetups, and technical sessions — online or in person. Enroll from the event page. Online links are shared after approval.
              </p>
            </div>
            <div className="relative">
              <div className="relative overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
                <img
                  src={COURSE_IMAGES.eventsHero}
                  alt="Engineers gathering for a technical workshop"
                  className="w-full h-[12.6rem] sm:h-[15.3rem] lg:h-[18rem] object-cover object-center"
                />
                <div className={heroImageFade('events')} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <CatalogFilterLayout
          applied={[
            ...(search.trim() ? [{ key: 'search', label: `Search: ${search.trim()}`, onRemove: () => setSearch('') }] : []),
            ...(categoryId ? [{ key: 'category', label: `Category: ${categories.find((c) => String(c.id) === String(categoryId))?.name || categoryId}`, onRemove: () => setCategoryId('') }] : []),
          ]}
          onClearFilters={() => {
            setSearch('');
            setCategoryId('');
          }}
          results={(
            <>
              {loading ? <PageLoading label="Loading events…" /> : null}
              {error && <p className="text-error-600">{error}</p>}
              {!loading && events.length === 0 && <p className="text-gray-500">No published events yet.</p>}
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {events.map((item) => (
                  <Link key={item.id} to={`/events/${item.slug}`}>
                    <Card className="h-full overflow-hidden" hoverable>
                      <CourseCoverImage
                        src={item.coverImageUrl}
                        fallbackSrc={COURSE_IMAGES.eventsHero}
                        className="h-40 w-full object-cover bg-gray-100"
                      />
                      <div className="p-5">
                      <p className="text-xs uppercase tracking-wide text-primary-600 inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" /> {item.mode}{item.categoryName ? ` · ${item.categoryName}` : ''}
                      </p>
                      <h2 className="mt-2 text-lg font-semibold text-gray-900">{item.title}</h2>
                      <p className="mt-2 text-sm text-gray-700 inline-flex items-start gap-1.5">
                        <Calendar className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" />
                        {formatEventWhen(item.startsAt, item.endsAt)}
                      </p>
                      {speakerHeadline(item) && (
                        <p className="mt-1 text-sm text-gray-800 inline-flex items-start gap-1.5">
                          <User className="h-4 w-4 mt-0.5 shrink-0 text-gray-400" />
                          {speakerHeadline(item)}
                        </p>
                      )}
                      <p className="mt-2 text-sm text-gray-600 line-clamp-3">{item.summary || item.subtitle}</p>
                      <p className="mt-3 text-sm text-gray-500 inline-flex items-center gap-1.5">
                        <MapPin className="h-4 w-4" />
                        {item.location || (item.mode === 'Online' ? 'Online' : 'Location announced later')}
                      </p>
                      <p className="mt-2 text-sm font-semibold text-gray-900"><CourseFee fee={Number(item.price || 0)} discount={0} /></p>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </>
          )}
        >
          <Input id="event-search" label="Search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events" />
          <CatalogFilterSelect label="Category" value={categoryId} onChange={setCategoryId}>
            <option value="">All</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </CatalogFilterSelect>
        </CatalogFilterLayout>
      </div>
    </div>
  );
};

export default EventsPage;
